/**
 * =========================================================================
 * SUPERVISOR NAVIGATION CONFIGURATION
 * =========================================================================
 * 
 * This file defines the navigation menu items for supervisor users.
 * Each navigation item includes permission checks to ensure users only
 * see menu items they have access to.
 * 
 * Navigation Structure:
 * - Dashboard (always visible)
 * - Members (if has member permissions)
 * - Reports (if has report permissions) 
 * - Settings (if has settings permissions)
 * 
 * Permission System:
 * - manage_members: Can edit member roles and manage team
 * - view_members: Can view team members (read-only)
 * - view_reports: Can access reports section
 * - manage_settings: Can access settings
 * =========================================================================
 */

import { useTranslation } from 'react-i18next';

// Static navigation structure without translations (for components that need static access)
const supervisorNavStructure = [
  {
    key: "dashboard",
    path: "/supervisor/dashboard",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="3" width="7" height="7"></rect>
      <rect x="14" y="3" width="7" height="7"></rect>
      <rect x="14" y="14" width="7" height="7"></rect>
      <rect x="3" y="14" width="7" height="7"></rect>
    </svg>`
  },
  {
    key: "chat",
    path: "/supervisor/chat",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>`
  },
  {
    key: "members",
    path: "/supervisor/members",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>`
  },
  {
    key: "reports",
    path: "/supervisor/reports",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="9" y1="9" x2="15" y2="15"></line>
      <line x1="15" y1="9" x2="9" y2="15"></line>
    </svg>`
  },
  {
    key: "settings",
    path: "/supervisor/settings",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1m19-4.8L18.4 8.8m-2.8 6.4L12.8 12m-1.6 3.2L8.4 12m6.4-2.8L12 15.2M4.2 9.2l2.8 2.8m6.4 2.8l2.8 2.8M9.2 4.2l2.8 2.8"></path>
    </svg>`
  }
];

// Hook to get translated navigation items
export const useSupervisorNav = () => {
  const { t } = useTranslation();
  
  const translationKeys = {
    dashboard: "navigation.dashboard",
    chat: "navigation.chat", 
    members: "navigation.members",
    reports: "navigation.reports",
    settings: "navigation.settings"
  };
  
  return supervisorNavStructure.map(item => ({
    ...item,
    name: t(translationKeys[item.key])
  }));
};

// Legacy static export for backwards compatibility
const supervisorNav = supervisorNavStructure.map(item => ({
  ...item,
  name: item.key.charAt(0).toUpperCase() + item.key.slice(1) // Fallback to capitalized key
}));

export default supervisorNav;
export { supervisorNavStructure }; 